import { Row, Col, Form, Input, Select, AutoComplete, Switch, Upload } from "antd";
import { useAntdAppMessage } from "../../hooks/useAntdAppMessage";
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { UserOutlined, UploadOutlined } from "@ant-design/icons";
import styles from "./user.module.scss";
import CustomButton from "../../components/Button";
import {
  RevisdomAPIViewModelsUserCreateUserRequestRoleEnum,
  type RevisdomAPIViewModelsUserCreateUserRequest,
} from "../../service/models/RevisdomAPIViewModelsUserCreateUserRequest";
import type { RevisdomAPIViewModelsOrganizationOrganizationResponse } from "../../service/models/RevisdomAPIViewModelsOrganizationOrganizationResponse";
import { useSecureService } from "../../store/service";
import { mapCreateUserFormRoleToWire } from "../../constants/roles";

interface OrganizationOption {
  value: string;
  label: string;
  id: string;
}

export type AddUserFormProps = {
  /** Başarılı oluşturma sonrası (ör. modal kapat, listeyi yenile) */
  onUserCreated?: () => void | Promise<void>;
  /** İptal (modal kapat veya sayfadan çık) */
  onCancel?: () => void;
  /** Ayarlar modalı: koyu tema + alt gradient şerit */
  darkAppearance?: boolean;
};

export function AddUserForm({ onUserCreated, onCancel, darkAppearance = false }: AddUserFormProps) {
  const message = useAntdAppMessage();
  const { getOrganizations, createUser } = useSecureService();
  const [form] = Form.useForm();
  const [organizations, setOrganizations] = useState<RevisdomAPIViewModelsOrganizationOrganizationResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [orgsListLoading, setOrgsListLoading] = useState(false);
  const [sendPasswordWithEmail, setSendPasswordWithEmail] = useState(true);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const avatarObjectUrlRef = useRef<string | null>(null);
  const organizationNameWatch = Form.useWatch("organizationName", form);

  const clearAvatarPreview = useCallback(() => {
    if (avatarObjectUrlRef.current) {
      URL.revokeObjectURL(avatarObjectUrlRef.current);
      avatarObjectUrlRef.current = null;
    }
    setAvatarPreview(null);
  }, []);

  const handleSessionExpired = () => {
    message.error("Session expired. Please log in again.");
  };

  const fetchOrganizations = async () => {
    try {
      setOrgsListLoading(true);
      const response = await getOrganizations();
      if (response.success && response.data) {
        setOrganizations(response.data);
      } else {
        message.warning("Organization list is empty.");
      }
    } catch {
      handleSessionExpired();
    } finally {
      setOrgsListLoading(false);
    }
  };

  useEffect(() => {
    void fetchOrganizations();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount only
  }, []);

  useEffect(() => {
    return () => {
      if (avatarObjectUrlRef.current) {
        URL.revokeObjectURL(avatarObjectUrlRef.current);
      }
    };
  }, []);

  const orgSearchNeedle = (typeof organizationNameWatch === "string" ? organizationNameWatch : "") || "";
  const orgOptions: OrganizationOption[] = useMemo(
    () =>
      organizations
        .filter(
          (org) => !orgSearchNeedle.trim() || (org.name || "").toLowerCase().includes(orgSearchNeedle.toLowerCase())
        )
        .map((org) => ({
          value: org.name || "",
          label: org.name || "",
          id: org.id?.toString() || "",
        })),
    [organizations, orgSearchNeedle]
  );

  const beforeAvatarUpload = (file: File) => {
    clearAvatarPreview();
    const url = URL.createObjectURL(file);
    avatarObjectUrlRef.current = url;
    setAvatarPreview(url);
    return Upload.LIST_IGNORE;
  };

  const onFinish = async (values: Record<string, string>) => {
    try {
      setLoading(true);
      const roleWire = mapCreateUserFormRoleToWire(values.role);
      const userData = {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        password: values.password,
        title: values.title?.trim() ? values.title.trim() : undefined,
        role: roleWire as RevisdomAPIViewModelsUserCreateUserRequest["role"],
        organizationName: typeof values.organizationName === "string" ? values.organizationName.trim() : undefined,
        sendPasswordWithEmail,
        notificationToEmail: true,
      } as RevisdomAPIViewModelsUserCreateUserRequest;
      const response = await createUser(userData);
      if (response.success) {
        message.success("User created successfully");
        form.resetFields();
        setSendPasswordWithEmail(true);
        clearAvatarPreview();
        if (onUserCreated) {
          await onUserCreated();
        }
      } else {
        message.error(response.message || "An error occurred while creating the user");
      }
    } catch {
      message.error("An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const roleOptions = Object.entries(RevisdomAPIViewModelsUserCreateUserRequestRoleEnum).map(([, value]) => ({
    label: value,
    value,
  }));

  const footerClass = darkAppearance ? styles.addUserFormFooterDark : styles.addUserFormFooterLight;
  const cancelBtnClass = darkAppearance ? styles.addUserFormCancelDark : styles.addUserFormCancelLight;
  const submitBtnClass = darkAppearance ? styles.addUserFormSubmitDark : undefined;

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={onFinish}
      className={darkAppearance ? styles.addUserFormDark : styles.addUserFormLight}
      initialValues={{ title: "" }}
      requiredMark={false}
    >
      <div className={styles.addUserProfileRow}>
        <div className={styles.addUserAvatar} aria-hidden>
          {avatarPreview ? (
            <img src={avatarPreview} alt="" />
          ) : (
            <UserOutlined className={styles.addUserAvatarIcon} />
          )}
        </div>
        <Upload accept="image/*" showUploadList={false} beforeUpload={beforeAvatarUpload}>
          <button type="button" className={styles.addUserUploadButton}>
            <UploadOutlined />
            <span>Upload</span>
          </button>
        </Upload>
      </div>

      <Row gutter={[16, 0]}>
        <Col xs={12} sm={12}>
          <Form.Item
            label="First Name"
            name="firstName"
            rules={[{ required: true, message: "Please enter the first name" }]}
          >
            <Input placeholder="Enter First Name" autoComplete="given-name" />
          </Form.Item>
        </Col>
        <Col xs={12} sm={12}>
          <Form.Item
            label="Last Name"
            name="lastName"
            rules={[{ required: true, message: "Please enter the last name" }]}
          >
            <Input placeholder="Enter Last Name" autoComplete="family-name" />
          </Form.Item>
        </Col>
        <Col xs={12} sm={12}>
          <Form.Item
            label="Password"
            name="password"
            rules={[
              { required: true, message: "Please enter the password" },
              {
                pattern: /^(?=.*[A-Z])(?=.*[!@#$%^&*(),.?":{}|<>]).{8,}$/,
                message:
                  "Password must be at least 8 characters, contain at least 1 uppercase letter and 1 special character",
              },
            ]}
          >
            <Input.Password placeholder="Enter Password" autoComplete="new-password" />
          </Form.Item>
        </Col>
        <Col xs={12} sm={12}>
          <Form.Item
            label="Re-Enter Password"
            name="confirmPassword"
            dependencies={["password"]}
            rules={[
              { required: true, message: "Please re-enter the password" },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("password") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error("Passwords do not match"));
                },
              }),
            ]}
          >
            <Input.Password placeholder="Re-Enter Password" autoComplete="new-password" />
          </Form.Item>
        </Col>
        <Col xs={12} sm={12}>
          <Form.Item
            label="Email"
            name="email"
            rules={[
              { required: true, message: "Please enter the email" },
              { type: "email", message: "Please enter a valid email address" },
            ]}
          >
            <Input placeholder="Enter Email" autoComplete="email" />
          </Form.Item>
        </Col>
        <Col xs={12} sm={12}>
          <Form.Item label="Role" name="role" rules={[{ required: true, message: "Please select a role" }]}>
            <Select placeholder="Select Role" options={roleOptions} popupMatchSelectWidth={false} />
          </Form.Item>
        </Col>
        <Col xs={12} sm={12}>
          <Form.Item name="title" label="Title">
            <Input placeholder="Job title (optional)" autoComplete="organization-title" />
          </Form.Item>
        </Col>
        <Col xs={12} sm={12}>
          <Form.Item
            label="Organization"
            name="organizationName"
            rules={[
              {
                validator: (_: unknown, v: unknown) => {
                  if (v == null || (typeof v === "string" && v.trim() === "")) {
                    return Promise.reject(new Error("Please enter the organization"));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <AutoComplete
              options={orgOptions}
              style={{ width: "100%" }}
              notFoundContent={orgsListLoading ? "Loading..." : "No match — current text will be sent"}
              onSelect={(value) => {
                form.setFieldValue("organizationName", value);
              }}
            >
              <Input allowClear maxLength={256} placeholder="Type or pick an organization" />
            </AutoComplete>
          </Form.Item>
        </Col>
      </Row>

      <div className={styles.addUserSwitchRow}>
        <span className={styles.addUserSwitchLabel}>Send user information with Email:</span>
        <Switch checked={sendPasswordWithEmail} onChange={setSendPasswordWithEmail} />
      </div>

      <div className={footerClass}>
        {onCancel ? (
          <CustomButton type="default" onClick={onCancel} className={cancelBtnClass}>
            Cancel
          </CustomButton>
        ) : null}
        <CustomButton type="primary" htmlType="submit" loading={loading} className={submitBtnClass}>
          Create User
        </CustomButton>
      </div>
    </Form>
  );
}
